import type { AutoResumeLatchReason } from '@core/realtime/autoResumeLatch';
import { isSocketUp, type RelayConnState } from './cqgRelay';

/**
 * Pure CQG UI-state derivations, extracted so the regression class we shipped fixes for
 * (badge claiming live over a dead socket; login form shown while connected; quote-tick log
 * spam) stays pinned by unit tests (`cqgUiState.test.ts`) — no React/socket mocking needed.
 */

/** BE session state as serialised by /api/v1/cqg/status + the `cqg.state` push (camelCase). */
export type CqgBeState = 'disconnected' | 'connecting' | 'live' | 'error';

/**
 * What BOTH the topbar badge AND the connector dialog display — the SINGLE source of truth for CQG
 * connection status, so the icon and the window are always on the same page. The badge renders the pill
 * for this value; the dialog renders the matching view (live → Connected, connecting → Connecting,
 * error/disconnected → login form).
 *
 * A faithful, real-time read of the ACTUAL end-to-end status, combining the BE session state with THIS
 * tab's browser→CQG socket:
 *   - **error** wins — a rejected/failed logon (e.g. code 101) shows the red error badge, never live;
 *   - **live** only when the BE session is live AND this tab's socket is actually OPEN (data can flow);
 *   - **connecting** while logging on / the socket is opening — INCLUDING a live session whose socket
 *     is closed/opening (e.g. just after a refresh, before auto-resume re-opens the pipe — a live
 *     session over a dead socket is not really live);
 *   - **disconnected** otherwise.
 *
 * An open socket alone is NOT "live" (CQG accepts the WebSocket before auth — a rejected logon leaves
 * it open), and a live session alone is NOT "live" if this tab's socket is down. Both must hold.
 */
export function deriveBadgeDisplay(
  beState: CqgBeState, socket: RelayConnState, reason?: string | null, passive = false,
): CqgBeState {
  // A TRANSPORT give-up is not a rejection (2026-08-25) — read it as "not connected", never as the
  // red alarm reserved for credentials CQG actually refused. See isTransportError below.
  const effective = isTransportError(beState, reason) ? 'disconnected' : beState;
  if (effective === 'error') return 'error';
  // Another tab owns the socket (D1, 2026-09-13): the BE session is live THERE — report it as connected,
  // not as this tab's dead pipe (which would read "connecting" and arm the retry ladder against the owner).
  if (effective === 'live' && passive) return 'live';
  // `open` (Logon written) and `live` (Logon acked on this socket, 2026-09-13) both mean data can flow
  // for the badge; the BE only reports `live` after the same LogonResult anyway.
  if (effective === 'live') return isSocketUp(socket) ? 'live' : 'connecting';
  if (effective === 'connecting' || socket === 'opening') return 'connecting';
  return 'disconnected';
}

/**
 * `reason` values the BE stamps on an Error state to mark it a TRANSPORT failure rather than a
 * credential rejection — mirrors `CqgUserSession.TransportGiveUpReason` / `TransportFailureReason` /
 * `LogonTimeoutReason` (journal-backend). Keep the two lists in step.
 */
export const TRANSPORT_ERROR_REASONS = ['idle_timeout', 'transport', 'logon_timeout'] as const;

/**
 * True when the BE reports `error` but the cause was the PIPE, not the credentials (2026-08-25 prod
 * incident): a browser that vanished without a clean close left the session Live over a dead socket,
 * the idle watchdog burned its 5 reconnects into an empty SignalR group and gave up — and because
 * that produced the same bare `error` a rejected logon produces, the reconnect-storm latch refused
 * to retry. The user sat on a red "Error" badge for 4h46m without ever clicking Connect.
 *
 * Only credential rejections (CQG code 101, and the concurrent-session 105 which owns its own
 * take-over prompt) may latch. Anything the BE marks as transport stays resumable.
 */
export function isTransportError(beState: CqgBeState, reason?: string | null): boolean {
  return beState === 'error'
    && !!reason
    && (TRANSPORT_ERROR_REASONS as readonly string[]).includes(reason);
}

/**
 * Whether a lost/rejected CQG session may be auto-resumed with the stored credentials.
 *
 * The crux of the reconnect-storm fix: a `code 101` logon rejection surfaces as `beState === 'error'`
 * (the BE emits 'error' ONLY for a rejected/forced logon — a transient socket drop is 'disconnected',
 * and a live-session-over-dead-pipe is 'live'). Re-sending the SAME rejected credentials on every
 * SignalR blip hammered CQG hundreds of times and risks locking the account. So a terminal rejection
 * ('error') is NOT auto-resumable — the user must explicitly Connect again; a transient loss still is.
 */
export function shouldAutoResume(
  beState: CqgBeState, reason?: string | null, latch: AutoResumeLatchReason | null = null,
): boolean {
  // The durable latch (2026-09-13 D4): a recorded kick, rejection or user Disconnect refuses
  // auto-resume whatever the BE state says — the BE may already report `disconnected` when a
  // reloaded tab asks, and that reload → re-logon is the loop this guards against.
  if (latch) return false;
  // A TRANSPORT error is resumable (2026-08-25): the latch exists to stop re-sending REJECTED
  // credentials, not to punish a dead pipe. Bare `error` (no reason) stays terminal — that is the
  // code-101 rejection shape, so the conservative default is unchanged. A kick (`kicked` /
  // `concurrent_session`) is an error with a non-transport reason, so it stays terminal too.
  if (isTransportError(beState, reason)) return true;
  return beState !== 'error';
}

/**
 * A session the BE still holds (live or mid-logon) whose browser socket in THIS tab is gone — the
 * "dead pipe" (2026-08-24 prod incident: a deploy roll dropped the socket minutes after a
 * successful logon and the dialog sat on "Connecting…" indefinitely). The badge honestly reads
 * 'connecting', but nothing is actually connecting unless someone retries: the page-load
 * auto-resume is once-per-load, and the BE pushes CqgReconnectNeeded only when IT lost the session
 * — with the session alive BE-side, no push ever comes, and the idle watchdog needs 15 minutes.
 * 'error' is deliberately excluded (the reconnect-storm latch: a rejected logon must never
 * self-retry), 'disconnected' too (nothing to resume — the login form is the right UI), and an
 * 'opening'/'open' socket is in-flight or healthy, not dead.
 */
export function isDeadPipe(
  beState: CqgBeState, socket: RelayConnState, reason?: string | null,
): boolean {
  if (socket !== 'closed') return false;
  if (beState === 'live' || beState === 'connecting') return true;
  // A transport-marked error is a dead pipe the BE has already given up on — the ladder is the only
  // thing left that will re-establish it mid-session (the page-load resume is once per load).
  return isTransportError(beState, reason);
}

/**
 * True when an inbound CQG frame carries ONLY live quote ticks (e.g. "RealTimeMarketData" /
 * "RealTimeMarketData×6"). These arrive several times per second while quotes stream, so the relay
 * excludes them from console/BE logging — everything else (logon, order statuses, snapshots…)
 * still logs.
 */
export function isPureMarketData(summary: string): boolean {
  return /^RealTimeMarketData(×\d+)?$/.test(summary);
}
